import { getCurrentUserId } from '../utils/auth_utils.js';
import { fetchBlockedContent } from './moderationApi.js';

const toIdSet = (ids) => new Set((ids || []).map((id) => String(id)).filter(Boolean));

let characterIds = new Set();
let scenarioIds = new Set();
let loaded = false;
let loadPromise = null;
const listeners = new Set();

const notify = (detail) => {
  listeners.forEach((listener) => {
    try {
      listener(detail);
    } catch (error) {
      console.warn('[blockedContentStore] listener failed:', error);
    }
  });
  document.dispatchEvent(new CustomEvent('blocked-content:changed', { detail }));
};

export const getBlockedFingerprint = () => {
  const characters = [...characterIds].sort().join(',');
  const scenarios = [...scenarioIds].sort().join(',');
  return `${characters}|${scenarios}`;
};

export const isCharacterBlocked = (id) => {
  if (id == null || id === '') return false;
  return characterIds.has(String(id));
};

export const isScenarioBlocked = (id) => {
  if (id == null || id === '') return false;
  return scenarioIds.has(String(id));
};

export const filterBlockedCharacters = (items, getId = (item) => item?.id) =>
  (Array.isArray(items) ? items : []).filter((item) => !isCharacterBlocked(getId(item)));

export const filterBlockedScenarios = (items, getId = (item) => item?.id ?? item?.scenario_id) =>
  (Array.isArray(items) ? items : []).filter((item) => !isScenarioBlocked(getId(item)));

export const getBlockedCharacterIds = () => [...characterIds];
export const getBlockedScenarioIds = () => [...scenarioIds];

export const subscribeBlockedContent = (listener) => {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const clearBlockedContent = () => {
  characterIds = new Set();
  scenarioIds = new Set();
  loaded = false;
  loadPromise = null;
  notify({ action: 'clear' });
};

const applyLists = (characters, scenarios, action = 'load') => {
  characterIds = toIdSet(characters);
  scenarioIds = toIdSet(scenarios);
  loaded = true;
  notify({ action, characters: getBlockedCharacterIds(), scenarios: getBlockedScenarioIds() });
};

export const markContentBlocked = (contentKind, contentId) => {
  const id = String(contentId);
  if (contentKind === 'character') characterIds.add(id);
  else if (contentKind === 'scenario') scenarioIds.add(id);
  notify({ action: 'block', kind: contentKind, id });
};

export const markContentUnblocked = (contentKind, contentId) => {
  const id = String(contentId);
  if (contentKind === 'character') characterIds.delete(id);
  else if (contentKind === 'scenario') scenarioIds.delete(id);
  notify({ action: 'unblock', kind: contentKind, id });
};

export const ensureBlockedContentLoaded = async () => {
  if (!getCurrentUserId()) {
    if (characterIds.size || scenarioIds.size || loaded) clearBlockedContent();
    return { characters: [], scenarios: [] };
  }
  if (loaded) {
    return { characters: getBlockedCharacterIds(), scenarios: getBlockedScenarioIds() };
  }
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      const result = await fetchBlockedContent();
      applyLists(result.characters, result.scenarios, 'load');
      return result;
    } catch (error) {
      console.warn('[blockedContentStore] Failed to load blocked content:', error);
      applyLists([], [], 'load');
      return { characters: [], scenarios: [] };
    } finally {
      loadPromise = null;
    }
  })();

  return loadPromise;
};

export const refreshBlockedContent = async () => {
  loaded = false;
  loadPromise = null;
  return ensureBlockedContentLoaded();
};
